/* ========================================
   PERA ATELIER — Core Theme JavaScript v1.1
   ======================================== */

(function () {
  'use strict';

  var Pera = window.Pera || {};

  /* ---- Utility Functions ---- */
  Pera.utils = {
    debounce: function (fn, delay) {
      delay = delay || 250;
      var timer;
      return function () {
        var args = arguments;
        var ctx = this;
        clearTimeout(timer);
        timer = setTimeout(function () { fn.apply(ctx, args); }, delay);
      };
    },
    formatMoney: function (cents) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      cents = parseInt(cents) || 0;
      var amount = (cents / 100).toFixed(2);
      return '<span class="aed-sym" style="font-family:\'AEDFont\',sans-serif!important;font-weight:normal;">D</span>' + amount;
    },
    getCookie: function (name) {
      var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
      return match ? match[2] : null;
    },
    setCookie: function (name, value, days) {
      var maxAge = days * 24 * 60 * 60;
      document.cookie = name + '=' + value + ';path=/;max-age=' + maxAge;
    },
    trapFocus: function (container) {
      var focusable = container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (!first) return;
      container.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab') return;
        if (e.shiftKey) {
          if (document.activeElement === first) { last.focus(); e.preventDefault(); }
        } else {
          if (document.activeElement === last) { first.focus(); e.preventDefault(); }
        }
      });
    }
  };

  /* ---- Sticky + Transparent Header ---- */
  Pera.StickyHeader = {
    init: function () {
      var header = document.getElementById('SiteHeader');
      if (!header) return;
      var isTransparent = header.classList.contains('site-header--transparent');
      var isSticky = Pera.settings.stickyHeader;
      if (!isSticky && !isTransparent) return;
      var lastScroll = 0;
      var ticking = false;
      window.addEventListener('scroll', function () {
        lastScroll = window.pageYOffset;
        if (!ticking) {
          window.requestAnimationFrame(function () {
            header.classList.toggle('is-scrolled', lastScroll > 10);
            ticking = false;
          });
          ticking = true;
        }
      });
    }
  };

  /* ---- Back to Top ---- */
  Pera.BackToTop = {
    init: function () {
      if (!Pera.settings.backToTop) return;
      var btn = document.getElementById('BackToTop');
      if (!btn) return;
      window.addEventListener('scroll', Pera.utils.debounce(function () {
        btn.classList.toggle('is-visible', window.pageYOffset > 400);
      }, 100));
      btn.addEventListener('click', function () {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  };

  /* ---- Mobile Menu ---- */
  Pera.MobileMenu = {
    init: function () {
      var toggle = document.getElementById('MobileMenuToggle');
      var menu = document.getElementById('MobileMenu');
      var close = document.getElementById('MobileMenuClose');
      if (!toggle || !menu) return;
      var backdrop = document.getElementById('MobileMenuBackdrop');

      var openMenu = function () {
        menu.classList.add('is-open');
        menu.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        if (backdrop) backdrop.classList.add('is-active');
        Pera.utils.trapFocus(menu);
      };
      var closeMenu = function () {
        menu.classList.remove('is-open');
        menu.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        if (backdrop) backdrop.classList.remove('is-active');
      };

      toggle.addEventListener('click', openMenu);
      if (close) close.addEventListener('click', closeMenu);
      if (backdrop) backdrop.addEventListener('click', closeMenu);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && menu.classList.contains('is-open')) closeMenu();
      });

      // Mobile submenu toggles
      menu.querySelectorAll('.mobile-nav__toggle').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var sub = btn.nextElementSibling;
          if (!sub) return;
          var isOpen = sub.style.maxHeight && sub.style.maxHeight !== '0px';
          btn.classList.toggle('is-open', !isOpen);
          sub.style.maxHeight = isOpen ? '0px' : sub.scrollHeight + 'px';
          btn.setAttribute('aria-expanded', !isOpen);
        });
      });

      // Close menu when any nav link is tapped (standard mobile UX)
      menu.querySelectorAll('.mobile-nav__link, .mobile-nav__sublink').forEach(function(link) {
        link.addEventListener('click', closeMenu);
      });
    }
  };

  /* ---- Cart API helpers ---- */
  Pera.CartAPI = {
    get: function () {
      return fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } }).then(function (r) { return r.json(); });
    },
    add: function (formData) {
      return fetch('/cart/add.js', { method: 'POST', body: formData }).then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'Could not add to cart'); });
        return r.json();
      });
    },
    change: function (key, quantity) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      }).then(function (r) { return r.json(); });
    },
    remove: function (key) {
      return Pera.CartAPI.change(key, 0);
    },
    updateNote: function (note) {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note })
      }).then(function (r) { return r.json(); });
    }
  };

  /* ---- Cart Drawer ---- */
  Pera.CartDrawer = {
    drawer: null,
    backdrop: null,
    isOpen: false,
    init: function () {
      if (Pera.settings.cartType !== 'drawer') return;
      this.drawer = document.getElementById('CartDrawer');
      this.backdrop = document.getElementById('CartDrawerBackdrop');
      if (!this.drawer) return;
      var self = this;

      // Toggle buttons (header cart icon)
      document.querySelectorAll('[data-cart-toggle]').forEach(function (btn) {
        btn.addEventListener('click', function (e) { e.preventDefault(); self.open(); });
      });

      // Close buttons inside drawer — use delegation so they survive cart refresh
      this.drawer.addEventListener('click', function (e) {
        if (e.target.closest('.cart-drawer__close')) { self.close(); }
      });

      // Backdrop click
      if (this.backdrop) this.backdrop.addEventListener('click', function () { self.close(); });

      // Escape key
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.isOpen) self.close();
      });

      // Remove item buttons (delegated)
      this.drawer.addEventListener('click', function (e) {
        var removeBtn = e.target.closest('[data-remove-item]');
        if (removeBtn) {
          e.preventDefault();
          var key = removeBtn.dataset.removeItem;
          removeBtn.disabled = true;
          removeBtn.textContent = '...';
          Pera.CartAPI.remove(key).then(function () {
            self.refresh();
          }).catch(function () {
            removeBtn.disabled = false;
            removeBtn.textContent = 'Remove';
          });
        }
      });

      // Quantity change in drawer (delegated on input change)
      this.drawer.addEventListener('change', function (e) {
        var input = e.target.closest('.cart-item__qty-input');
        if (!input) return;
        var key = input.dataset.itemKey;
        var qty = parseInt(input.value) || 0;
        if (!key) return;
        Pera.CartAPI.change(key, qty).then(function () { self.refresh(); });
      });
    },
    open: function () {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      if (this.backdrop) this.backdrop.classList.add('is-active');
      document.body.style.overflow = 'hidden';
      this.isOpen = true;
      Pera.utils.trapFocus(this.drawer);
    },
    close: function () {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      if (this.backdrop) this.backdrop.classList.remove('is-active');
      document.body.style.overflow = '';
      this.isOpen = false;
    },
    refresh: function () {
      var self = this;
      // Fetch updated cart section HTML via Shopify section rendering API
      return fetch('/?section_id=cart-drawer&t=' + Date.now())
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');

          // Replace the entire #CartDrawerItems element (incl. shipping bar inside it)
          var newEl = doc.getElementById('CartDrawerItems');
          var curEl = document.getElementById('CartDrawerItems');
          if (newEl && curEl) {
            curEl.innerHTML = newEl.innerHTML;
          }

          // Update count + subtotals via Cart API (single fetch)
          return Pera.CartAPI.get();
        })
        .then(function (cart) {
          // Item count badges
          document.querySelectorAll('[data-cart-count]').forEach(function (el) {
            el.textContent = cart.item_count;
            el.classList.toggle('is-empty', cart.item_count === 0);
          });
          // Subtotal display
          var formatted = Pera.utils.formatMoney(cart.total_price);
          document.querySelectorAll('[data-cart-subtotal]').forEach(function (el) {
            el.innerHTML = formatted;
          });
          document.querySelectorAll('[data-cart-subtotal-btn]').forEach(function (el) {
            el.innerHTML = formatted;
          });
          // Show/hide footer — use correct class name from cart-drawer snippet
          var footer = self.drawer ? (
            self.drawer.querySelector('.cdr__footer') ||
            self.drawer.querySelector('.cart-drawer__footer')
          ) : null;
          if (footer) footer.style.display = cart.item_count > 0 ? '' : 'none';
        })
        .catch(function (err) {
          console.warn('[CartDrawer] refresh failed:', err);
        });
    },
    update: function () {
      return this.refresh();
    }
  };

  /* ---- Product Form (Add to Cart) ---- */
  Pera.ProductForm = {
    init: function () {
      document.addEventListener('submit', function (e) {
        var form = e.target.closest('[data-product-form]');
        if (!form) return;
        e.preventDefault();
        var btn = form.querySelector('[type="submit"]');
        var originalText = btn ? btn.textContent : '';
        if (btn) { btn.disabled = true; btn.textContent = '...'; }

        var formData = new FormData(form);
        Pera.CartAPI.add(formData)
          .then(function () {
            if (btn) { btn.textContent = '✓ Added!'; }
            if (Pera.settings.cartType === 'drawer') {
              Pera.CartDrawer.refresh().then(function () {
                Pera.CartDrawer.open();
              });
            }
            setTimeout(function () {
              if (btn) { btn.textContent = originalText; btn.disabled = false; }
            }, 2000);
          })
          .catch(function (err) {
            if (btn) { btn.textContent = err.message || 'Error'; btn.disabled = false; }
            setTimeout(function () {
              if (btn) { btn.textContent = originalText; }
            }, 3000);
          });
      });
    }
  };

  /* ---- Quantity Buttons ---- */
  Pera.QuantityButtons = {
    init: function () {
      document.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-qty-change]');
        if (!btn) return;
        var input = btn.parentElement.querySelector('.cart-item__qty-input, input[name="quantity"]');
        if (!input) return;
        var val = parseInt(input.value) || 1;
        var newVal = btn.dataset.qtyChange === 'plus' ? val + 1 : Math.max(0, val - 1);
        input.value = newVal;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  };

  /* ---- Search Overlay ---- */
  Pera.SearchOverlay = {
    overlay: null,
    input: null,
    init: function () {
      this.overlay = document.getElementById('SearchOverlay');
      this.input = document.getElementById('SearchInput');
      if (!this.overlay) return;
      var self = this;

      document.querySelectorAll('[data-search-toggle]').forEach(function (btn) {
        btn.addEventListener('click', function (e) { e.preventDefault(); self.open(); });
      });

      var closeBtn = this.overlay.querySelector('.search-overlay__close');
      if (closeBtn) closeBtn.addEventListener('click', function () { self.close(); });

      this.overlay.addEventListener('click', function (e) {
        if (e.target === self.overlay) self.close();
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.overlay.classList.contains('is-active')) self.close();
      });

      if (Pera.settings.predictiveSearch && this.input) {
        this.input.addEventListener('input', Pera.utils.debounce(function (e) {
          self.search(e.target.value);
        }, 300));
      }
    },
    open: function () {
      this.overlay.classList.add('is-active');
      this.overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      var inputEl = this.input;
      if (inputEl) setTimeout(function () { inputEl.focus(); }, 50);
    },
    close: function () {
      this.overlay.classList.remove('is-active');
      this.overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },
    search: function (query) {
      var resultsEl = document.getElementById('PredictiveSearchResults');
      if (!resultsEl) return;
      if (!query || query.length < 2) { resultsEl.innerHTML = ''; return; }
      fetch('/search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          var products = (data.resources && data.resources.results && data.resources.results.products) || [];
          if (products.length === 0) {
            resultsEl.innerHTML = '<p style="color:var(--color-text-light);font-size:1.4rem;text-align:center;padding:2rem;">No results found.</p>';
            return;
          }
          resultsEl.innerHTML = products.map(function (p) {
            var imgSrc = p.image || '';
            var price = Pera.utils.formatMoney(p.price, Pera.settings.moneyFormat);
            return '<a href="' + p.url + '" class="predictive-result">' +
              '<div class="predictive-result__image">' + (imgSrc ? '<img src="' + imgSrc + '" alt="' + p.title + '" loading="lazy">' : '') + '</div>' +
              '<div class="predictive-result__info">' +
                '<div class="predictive-result__title">' + p.title + '</div>' +
                '<div class="predictive-result__price">' + price + '</div>' +
              '</div>' +
            '</a>';
          }).join('');
        })
        .catch(function () {});
    }
  };

  /* ---- Quick View ---- */
  Pera.QuickView = {
    _idx: 0,
    _images: [],
    _keyFn: null,

    _injectCss: function () {
      if (document.getElementById('qv-styles')) return;
      var s = document.createElement('style');
      s.id = 'qv-styles';
      s.textContent = [
        '.quick-view-modal{max-width:92rem;width:94%;max-height:90vh;overflow-y:auto;padding:3.2rem;}',
        '.qv{display:grid;grid-template-columns:1fr 1fr;gap:3.2rem;align-items:start;}',
        '.qv__gallery{display:flex;flex-direction:column;gap:.8rem;}',
        '.qv__main{position:relative;aspect-ratio:3/4;overflow:hidden;background:var(--color-background-secondary);}',
        '.qv__hero-img{width:100%;height:100%;object-fit:cover;display:block;transition:opacity .25s;}',
        '.qv__nav{position:absolute;top:50%;transform:translateY(-50%);background:rgba(255,255,255,.88);border:none;width:3.4rem;height:3.4rem;border-radius:50%;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background .2s;z-index:2;box-shadow:0 2px 8px rgba(0,0,0,.15);}',
        '.qv__nav:hover{background:#fff;}',
        '.qv__nav--prev{left:.8rem;}',
        '.qv__nav--next{right:.8rem;}',
        '.qv__thumbs{display:flex;flex-direction:row;gap:.5rem;flex-wrap:wrap;}',
        '.qv__thumb{border:none;background:none;padding:0;cursor:pointer;opacity:.5;transition:all .25s;position:relative;width:5.6rem;height:7rem;overflow:hidden;flex-shrink:0;}',
        '.qv__thumb::after{content:"";position:absolute;inset:0;border:2px solid var(--color-primary);opacity:0;transition:opacity .2s;pointer-events:none;}',
        '.qv__thumb.is-active{opacity:1;}.qv__thumb.is-active::after{opacity:1;}.qv__thumb:hover{opacity:1;}',
        '.qv__thumb img{width:100%;height:100%;object-fit:cover;display:block;}',
        '.qv__info{display:flex;flex-direction:column;gap:1.4rem;}',
        '.qv__vendor{font-size:1.1rem;letter-spacing:.15em;text-transform:uppercase;color:var(--color-text-light);margin:0;}',
        '.qv__title{font-size:clamp(1.8rem,2.5vw,2.8rem);line-height:1.15;margin:0;}',
        '.qv__title-link{color:inherit;text-decoration:none;}',
        '.qv__title-link:hover{color:var(--color-primary);}',
        '.qv__price{font-size:1.9rem;display:flex;gap:1rem;align-items:baseline;flex-wrap:wrap;}',
        '.qv__price-sale{color:var(--color-sale,#c0392b);font-weight:600;}',
        '.qv__price-compare{color:var(--color-text-light);text-decoration:line-through;font-size:1.4rem;}',
        '.qv__price-regular{font-weight:600;}',
        '.qv__desc{font-size:1.35rem;line-height:1.7;color:var(--color-text-light);margin:0;}',
        '.qv-opt{display:flex;flex-direction:column;gap:.6rem;}',
        '.qv-opt__label{font-size:1.15rem;font-weight:600;text-transform:uppercase;letter-spacing:.1em;}',
        '.qv-opt__val{font-weight:400;text-transform:none;}',
        '.qv-opt__pills{display:flex;flex-wrap:wrap;gap:.5rem;}',
        '.qv-pill{padding:.5rem 1.4rem;border:1.5px solid var(--color-border);font-size:1.25rem;cursor:pointer;transition:all .2s;background:#fff;color:var(--color-text);}',
        '.qv-pill:hover{border-color:var(--color-primary);}',
        '.qv-pill.is-active{border-color:var(--color-primary);background:var(--color-primary);color:#fff;}',
        '.qv__atc{width:100%;margin-top:.4rem;}',
        '.qv__view-full{font-size:1.25rem;color:var(--color-text-light);text-decoration:underline;transition:color .2s;display:inline-block;}',
        '.qv__view-full:hover{color:var(--color-primary);}',
        '@media(max-width:767px){.quick-view-modal{padding:2rem;max-height:94vh;}.qv{grid-template-columns:1fr;}.qv__thumb{width:4.8rem;height:6rem;}}'
      ].join('');
      document.head.appendChild(s);
    },

    init: function () {
      if (!Pera.settings.quickView) return;
      this._injectCss();
      var self = this;
      var modal = document.getElementById('QuickViewModal');
      var content = document.getElementById('QuickViewContent');
      if (!modal) return;

      // Open
      document.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-quick-view]');
        if (!btn) return;
        e.preventDefault();
        var url = btn.dataset.quickView;
        content.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;min-height:28rem;"><div class="spinner"></div></div>';
        modal.classList.add('is-active');
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';

        fetch(url + '?section_id=quick-view')
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var parser = new DOMParser();
            var doc = parser.parseFromString(html, 'text/html');
            var qv = doc.getElementById('QvProduct');
            content.innerHTML = '';
            if (qv) {
              content.appendChild(qv);
              // Transfer JSON data scripts (they don't execute — just transfer textContent)
              ['QvImagesData', 'QvVariantsData'].forEach(function (id) {
                var el = doc.getElementById(id);
                if (!el) return;
                var s = document.createElement('script');
                s.type = 'application/json';
                s.id = id;
                s.textContent = el.textContent;
                content.appendChild(s);
              });
            } else {
              content.innerHTML = '<p style="padding:4rem;text-align:center;">Could not load product.</p>';
              return;
            }
            self.initGallery();
            self.initVariants();
          })
          .catch(function () {
            content.innerHTML = '<p style="padding:4rem;text-align:center;">Could not load product.</p>';
          });
      });

      // Close button
      var closeBtn = modal.querySelector('.modal-close');
      if (closeBtn) closeBtn.addEventListener('click', function () { self.close(modal, content); });

      // Backdrop
      modal.addEventListener('click', function (e) { if (e.target === modal) self.close(modal, content); });

      // Escape key
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && modal.classList.contains('is-active')) self.close(modal, content);
      });
    },

    close: function (modal, content) {
      modal.classList.remove('is-active');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (content) content.innerHTML = '';
      this._images = [];
      this._idx = 0;
      if (this._keyFn) { document.removeEventListener('keydown', this._keyFn); this._keyFn = null; }
    },

    initGallery: function () {
      var self = this;
      var dataEl = document.getElementById('QvImagesData');
      if (!dataEl) return;
      try { self._images = JSON.parse(dataEl.textContent); } catch (e) { return; }
      if (!self._images.length) return;
      self._idx = 0;

      var heroImg = document.getElementById('QvHeroImg');

      function setImg(idx) {
        var total = self._images.length;
        self._idx = ((idx % total) + total) % total;
        if (heroImg && self._images[self._idx]) {
          heroImg.style.opacity = '0.55';
          heroImg.src = self._images[self._idx].src;
          heroImg.alt = self._images[self._idx].alt;
          heroImg.onload = function () { heroImg.style.opacity = '1'; };
        }
        document.querySelectorAll('.qv__thumb[data-qv-index]').forEach(function (t, i) {
          t.classList.toggle('is-active', parseInt(t.dataset.qvIndex) === self._idx);
        });
      }

      document.querySelectorAll('.qv__thumb[data-qv-index]').forEach(function (t) {
        t.addEventListener('click', function () { setImg(parseInt(t.dataset.qvIndex)); });
      });

      var prev = document.getElementById('QvPrev');
      var next = document.getElementById('QvNext');
      if (prev) prev.addEventListener('click', function () { setImg(self._idx - 1); });
      if (next) next.addEventListener('click', function () { setImg(self._idx + 1); });

      // Keyboard arrow nav (scoped to when modal is open)
      if (self._keyFn) document.removeEventListener('keydown', self._keyFn);
      self._keyFn = function (e) {
        var modal = document.getElementById('QuickViewModal');
        if (!modal || !modal.classList.contains('is-active')) return;
        if (e.key === 'ArrowLeft')  { e.preventDefault(); setImg(self._idx - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); setImg(self._idx + 1); }
      };
      document.addEventListener('keydown', self._keyFn);
    },

    initVariants: function () {
      var variantsEl = document.getElementById('QvVariantsData');
      if (!variantsEl) return;
      var variants;
      try { variants = JSON.parse(variantsEl.textContent); } catch (e) { return; }
      if (!variants || !variants.length) return;

      var selectedOptions = variants[0].options ? variants[0].options.slice() : [];

      // Sync from active pills
      document.querySelectorAll('.qv-opt__pills').forEach(function (wrap) {
        var active = wrap.querySelector('.qv-pill.is-active');
        if (active) selectedOptions[parseInt(wrap.dataset.qvOptPos || 1) - 1] = active.dataset.qvValue;
      });

      function findVariant() {
        return variants.find(function (v) {
          return v.options && v.options.every(function (o, i) { return o === selectedOptions[i]; });
        });
      }

      function updateVariant() {
        var v = findVariant();
        if (!v) return;
        var idEl = document.getElementById('QvVariantId');
        if (idEl) idEl.value = v.id;
        var atc = document.getElementById('QvATC');
        if (atc) {
          atc.disabled = !v.available;
          atc.textContent = v.available ? (atc.dataset.label || 'Add to Cart') : 'Sold Out';
        }
        var priceEl = document.getElementById('QvPrice');
        if (priceEl && v.price !== undefined) {
          var fmt = Pera.utils.formatMoney(v.price);
          if (v.compare_at_price && v.compare_at_price > v.price) {
            priceEl.innerHTML = '<span class="qv__price-sale" style="display:inline-flex;align-items:center;gap:2px;">' + fmt + '</span><span class="qv__price-compare" style="display:inline-flex;align-items:center;gap:2px;">' + Pera.utils.formatMoney(v.compare_at_price) + '</span>';
          } else {
            priceEl.innerHTML = '<span class="qv__price-regular" style="display:inline-flex;align-items:center;gap:2px;">' + fmt + '</span>';
          }
        }
        if (v.featured_image) {
          var heroImg = document.getElementById('QvHeroImg');
          if (heroImg) heroImg.src = v.featured_image.src || v.featured_image;
        }
      }

      document.querySelectorAll('.qv-opt__pills .qv-pill').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var wrap = btn.closest('.qv-opt__pills');
          wrap.querySelectorAll('.qv-pill').forEach(function (b) { b.classList.remove('is-active'); });
          btn.classList.add('is-active');
          var pos = parseInt(wrap.dataset.qvOptPos || 1) - 1;
          var lbl = document.getElementById('QvOptVal-' + pos);
          if (lbl) lbl.textContent = btn.dataset.qvValue;
          selectedOptions[pos] = btn.dataset.qvValue;
          updateVariant();
        });
      });
    }
  };

  /* ---- Wishlist (localStorage) ---- */
  Pera.Wishlist = {
    KEY: 'pera_wishlist_v1',
    DATA_KEY: 'pera_wishlist_data',
    init: function () {
      if (!Pera.settings.wishlist) return;
      var self = this;
      document.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-wishlist-toggle]');
        if (!btn) return;
        e.preventDefault();
        var id = String(btn.dataset.wishlistToggle);
        var productData = {
          id: id,
          handle: btn.dataset.wlHandle || '',
          title: btn.dataset.wlTitle || '',
          price: parseInt(btn.dataset.wlPrice) || 0,
          priceFmt: btn.dataset.wlPriceFmt || '',
          compare: parseInt(btn.dataset.wlCompare) || 0,
          compareFmt: btn.dataset.wlCompareFmt || '',
          image: btn.dataset.wlImage || '',
          url: btn.dataset.wlUrl || '',
          vendor: btn.dataset.wlVendor || '',
          available: btn.dataset.wlAvailable !== 'false'
        };
        self.toggle(id, productData);
        self.updateAll();
      });
      this.updateAll();
    },
    get: function () {
      try { return JSON.parse(localStorage.getItem(this.KEY)) || []; } catch (e) { return []; }
    },
    getData: function () {
      try { return JSON.parse(localStorage.getItem(this.DATA_KEY)) || {}; } catch (e) { return {}; }
    },
    has: function (id) { return this.get().indexOf(String(id)) > -1; },
    toggle: function (id, productData) {
      id = String(id);
      var list = this.get();
      var data = this.getData();
      var idx = list.indexOf(id);
      if (idx > -1) {
        list.splice(idx, 1);
        delete data[id];
      } else {
        list.push(id);
        if (productData) data[id] = productData;
      }
      localStorage.setItem(this.KEY, JSON.stringify(list));
      localStorage.setItem(this.DATA_KEY, JSON.stringify(data));
    },
    updateAll: function () {
      var self = this;
      document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
        btn.classList.toggle('is-wishlisted', self.has(btn.dataset.wishlistToggle));
        btn.setAttribute('aria-pressed', self.has(btn.dataset.wishlistToggle));
      });
      var count = this.get().length;
      document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
        el.textContent = count;
        el.classList.toggle('is-empty', count === 0);
      });
    }
  };


  /* ---- Newsletter Popup ---- */
  Pera.NewsletterPopup = {
    init: function () {
      if (!Pera.settings.newsletterPopup) return;
      if (Pera.utils.getCookie('pera_nl') === '1') return;
      var popup = document.querySelector('.newsletter-popup');
      if (!popup) return;
      // newsletterPopupDelay comes from Liquid as seconds, convert to ms
      var delaySeconds = Pera.settings.newsletterPopupDelay || 5;
      var delay = delaySeconds * 1000;
      setTimeout(function () {
        popup.classList.add('is-active');
        popup.setAttribute('aria-hidden', 'false');
        var closeBtn = popup.querySelector('.newsletter-popup__close');
        if (closeBtn) closeBtn.addEventListener('click', function () { Pera.NewsletterPopup.close(popup); });
        popup.addEventListener('click', function (e) { if (e.target === popup) Pera.NewsletterPopup.close(popup); });
        document.addEventListener('keydown', function (e) { if (e.key === 'Escape') Pera.NewsletterPopup.close(popup); });
      }, delay);
    },
    close: function (popup) {
      popup.classList.remove('is-active');
      popup.setAttribute('aria-hidden', 'true');
      Pera.utils.setCookie('pera_nl', '1', 7);
    }
  };

  /* ---- Age Verification ---- */
  Pera.AgeVerification = {
    init: function () {
      if (!Pera.settings.ageVerification) return;
      if (Pera.utils.getCookie('pera_age') === '1') return;
      var gate = document.querySelector('.age-gate');
      if (!gate) return;
      gate.style.display = 'flex';
      document.body.style.overflow = 'hidden';
      var confirmBtn = gate.querySelector('[data-age-confirm]');
      if (confirmBtn) confirmBtn.addEventListener('click', function () {
        gate.style.display = 'none';
        document.body.style.overflow = '';
        Pera.utils.setCookie('pera_age', '1', 30);
      });
      var denyBtn = gate.querySelector('[data-age-deny]');
      if (denyBtn) denyBtn.addEventListener('click', function () {
        window.location.href = 'https://www.google.com';
      });
    }
  };

  /* ---- Countdown Timer ---- */
  Pera.Countdown = {
    init: function () {
      document.querySelectorAll('[data-countdown]').forEach(function (el) {
        var endDate = el.dataset.countdown;
        if (!endDate) return;
        var end = new Date(endDate).getTime();
        if (isNaN(end)) return;
        var update = function () {
          var diff = end - Date.now();
          if (diff <= 0) { el.innerHTML = '<span class="countdown__expired">Sale Ended</span>'; return; }
          var d = Math.floor(diff / 86400000);
          var h = Math.floor((diff % 86400000) / 3600000);
          var m = Math.floor((diff % 3600000) / 60000);
          var s = Math.floor((diff % 60000) / 1000);
          el.innerHTML =
            '<div class="countdown__block"><span class="countdown__num">' + d + '</span><span class="countdown__label">Days</span></div>' +
            '<div class="countdown__sep">:</div>' +
            '<div class="countdown__block"><span class="countdown__num">' + String(h).padStart(2, '0') + '</span><span class="countdown__label">Hrs</span></div>' +
            '<div class="countdown__sep">:</div>' +
            '<div class="countdown__block"><span class="countdown__num">' + String(m).padStart(2, '0') + '</span><span class="countdown__label">Min</span></div>' +
            '<div class="countdown__sep">:</div>' +
            '<div class="countdown__block"><span class="countdown__num">' + String(s).padStart(2, '0') + '</span><span class="countdown__label">Sec</span></div>';
        };
        update();
        setInterval(update, 1000);
      });
    }
  };

  /* ---- Scroll Animations (IntersectionObserver) ---- */
  Pera.ScrollAnimations = {
    init: function () {
      if (!('IntersectionObserver' in window)) return;
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
      document.querySelectorAll('[data-animate]').forEach(function (el) { observer.observe(el); });
    }
  };

  /* ---- Predictive Search CSS injection ---- */
  Pera.SearchStyles = {
    init: function () {
      var style = document.createElement('style');
      style.textContent = '.predictive-result{display:flex;gap:1.2rem;align-items:center;padding:1rem;transition:background .2s;border-radius:2px}.predictive-result:hover{background:var(--color-background-secondary)}.predictive-result__image{width:5rem;height:6.5rem;flex-shrink:0;overflow:hidden}.predictive-result__image img{width:100%;height:100%;object-fit:cover}.predictive-result__title{font-size:1.3rem;font-weight:500;margin-bottom:.3rem}.predictive-result__price{font-size:1.2rem;color:var(--color-text-light)}';
      document.head.appendChild(style);
    }
  };

  /* ---- Init ---- */
  document.addEventListener('DOMContentLoaded', function () {
    Pera.SearchStyles.init();
    Pera.StickyHeader.init();
    Pera.BackToTop.init();
    Pera.MobileMenu.init();
    Pera.CartDrawer.init();
    Pera.SearchOverlay.init();
    Pera.QuickView.init();
    Pera.Wishlist.init();
    Pera.NewsletterPopup.init();
    Pera.AgeVerification.init();
    Pera.Countdown.init();
    Pera.ScrollAnimations.init();
    Pera.ProductForm.init();
    Pera.QuantityButtons.init();
  });

  window.Pera = Pera;
})();
